import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Money, OrderLine, Party, Product, SupplierDemandGroup } from '@prody/shared';
import {
  DEMAND_ORDER_STATUSES, buildSupplierDemand, groupDemandBySupplier, hasRole,
  mulMoney, primaryChannel,
} from '@prody/shared';
import {
  Button, CHANNEL_ICONS, Code, CrateHeader, CrateLabel, EmptyState, FactList,
  IconClose, IconPrint, Input, ListPane, ManifestPane, PageHeader, Section,
  Segmented, Sheet, Stamp, Toolbar, Workbench,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useOrders, useParties, useProducts, useQuotes } from '~/platform/data/queries';
import { useHasManifestSpace } from '~/platform/hooks/useMediaQuery';
import { ORDER_TONE, productName } from '~/platform/domain/presentation';
import './suppliers.css';

/* ==================================================================
   SUPPLIERS.

   The left side is the directory — every party wearing the supplier
   role, as a crate label with its stand code and run days. The right
   side is the sheet that used to be printed and handed over at 04:30:
   what this supplier needs to bring for the chosen day, straight from
   the day's confirmed order lines. No copy of the data exists here;
   change an order and the sheet already says so.
   ================================================================== */

type DayFilter = 'today' | 'tomorrow';

const addDays = (plain: string, days: number): string => {
  const at = new Date(`${plain}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
};

export default function SuppliersPage() {
  const { t } = useTranslation(['suppliers', 'grouping', 'purchasing', 'orders', 'contacts', 'common']);
  const { business, format } = useBusiness();
  const [dayFilter, setDayFilter] = useState<DayFilter>('today');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    // Deep link from global search: /suppliers?supplier=pty_x
    const fromUrl = new URLSearchParams(window.location.search).get('supplier');
    return fromUrl || null;
  });
  const hasManifestSpace = useHasManifestSpace();

  const today = format.tradingDay();
  const day = dayFilter === 'today' ? today : addDays(today, 1);

  const { data: parties = [], isLoading } = useParties(business.id, {
    role: 'supplier',
    ...(search.trim() ? { search: search.trim() } : {}),
  });
  const { data: orders = [] } = useOrders(business.id, {
    deliveryDate: day,
    statuses: [...DEMAND_ORDER_STATUSES],
  });
  const { data: products = [] } = useProducts(business.id);
  const { data: quotes = [] } = useQuotes(business.id, today);

  const suppliers = useMemo(() => parties.filter((p) => hasRole(p, 'supplier')), [parties]);
  const productsById = useMemo(
    () => new Map<string, Product>(products.map((p) => [p.id as string, p])),
    [products],
  );
  const suppliersById = useMemo(
    () => new Map(suppliers.map((s) => [s.id as string, s])),
    [suppliers],
  );

  const demandGroups = useMemo(() => {
    const resolve = (line: OrderLine) => {
      if (line.supplierId) {
        const party = suppliersById.get(line.supplierId as string);
        return { id: line.supplierId as string, name: line.supplierName ?? party?.name ?? '—' };
      }
      const fallback = productsById.get(line.productId)?.defaultSupplierId;
      if (!fallback) return null;
      const party = suppliersById.get(fallback as string);
      return party ? { id: party.id as string, name: party.name } : null;
    };
    return new Map(
      groupDemandBySupplier(buildSupplierDemand(orders, resolve)).map((g) => [g.supplierId, g]),
    );
  }, [orders, productsById, suppliersById]);

  const costFor = useMemo(
    () => new Map(quotes.map((q) => [q.productId as string, q.cost])),
    [quotes],
  );

  const selected = suppliers.find((s) => s.id === selectedId) ?? null;
  const selectedGroup = selected ? demandGroups.get(selected.id as string) ?? null : null;

  const manifest = selected ? (
    <SupplierManifest
      supplier={selected}
      group={selectedGroup}
      day={day}
      productsById={productsById}
      costFor={costFor}
      onClose={() => setSelectedId(null)}
    />
  ) : null;

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={isLoading ? undefined : t('common:labels.results', { count: suppliers.length })}
      />

      <Toolbar>
        <Segmented<DayFilter>
          label={t('orders:filters.day')}
          value={dayFilter}
          onChange={setDayFilter}
          options={[
            { value: 'today', label: t('common:labels.today') },
            { value: 'tomorrow', label: t('common:labels.tomorrow') },
          ]}
        />
        <Input
          type="search"
          value={search}
          placeholder={t('searchPlaceholder')}
          aria-label={t('common:actions.search')}
          onChange={(e) => setSearch(e.target.value)}
          style={{ inlineSize: 220 }}
        />
      </Toolbar>

      <ListPane>
        {!isLoading && suppliers.length === 0 ? (
          <EmptyState title={t('empty')} body={t('emptyBody')} />
        ) : (
          <div className="pd-sup__list">
            {suppliers.map((supplier) => {
              const group = demandGroups.get(supplier.id as string);
              const lineCount = group?.lines.length ?? 0;
              return (
                <CrateLabel
                  key={supplier.id}
                  handlingClass="neutral"
                  code={supplier.code}
                  title={supplier.name}
                  subtitle={
                    supplier.supplier
                      ? t(`sourceKind.${supplier.supplier.sourceKind}`)
                      : undefined
                  }
                  stamp={
                    lineCount > 0 ? (
                      <Stamp tone="accent" size="sm">
                        {t('needsToday', { count: lineCount })}
                      </Stamp>
                    ) : (
                      <Stamp tone="neutral" size="sm">{t('nothingToday')}</Stamp>
                    )
                  }
                  facts={[
                    ...(supplier.supplier?.standCode
                      ? [{ label: t('contacts:standCode'), value: <Code>{supplier.supplier.standCode}</Code> }]
                      : []),
                    ...(supplier.supplier?.runDays.length
                      ? [
                          {
                            label: t('contacts:runDays'),
                            value: (
                              <span className="num">
                                {supplier.supplier.runDays.map((d) => t(`weekday.${d}`)).join(' · ')}
                              </span>
                            ),
                          },
                        ]
                      : []),
                    ...(group
                      ? [
                          {
                            label: t('orders:title'),
                            value: <span className="num">{group.orderCount}</span>,
                          },
                        ]
                      : []),
                  ]}
                  onClick={() => setSelectedId(supplier.id as string)}
                  selected={selectedId === supplier.id}
                />
              );
            })}
          </div>
        )}
      </ListPane>

      {hasManifestSpace ? (
        <ManifestPane open={Boolean(selected)}>{manifest}</ManifestPane>
      ) : (
        <Sheet
          open={Boolean(selected)}
          onOpenChange={(open) => !open && setSelectedId(null)}
          title={selected?.name ?? ''}
        >
          {manifest}
        </Sheet>
      )}
    </Workbench>
  );
}

/* ------------------------------------------------------------------ *
 * The supplier's sheet: identity and channels up top, then the goods
 * for the chosen day with the orders behind each line one tap away.
 * Printing this pane is the hand-over that used to be a paper list.
 * ------------------------------------------------------------------ */

function SupplierManifest({
  supplier,
  group,
  day,
  productsById,
  costFor,
  onClose,
}: {
  supplier: Party;
  group: SupplierDemandGroup | null;
  day: string;
  productsById: Map<string, Product>;
  costFor: Map<string, Money>;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation(['suppliers', 'orders', 'contacts', 'common']);
  const { format } = useBusiness();
  const [openLine, setOpenLine] = useState<string | null>(null);

  const channel = primaryChannel(supplier);
  const estimated = (group?.lines ?? []).reduce((sum, row) => {
    const cost = costFor.get(row.productId);
    return cost ? sum + mulMoney(cost, row.total.value).minor : sum;
  }, 0);

  return (
    <div className="pd-sup__manifest">
      <CrateHeader
        handlingClass="neutral"
        code={supplier.code}
        title={supplier.name}
        subtitle={supplier.supplier ? t(`sourceKind.${supplier.supplier.sourceKind}`) : undefined}
        stamp={
          supplier.supplier?.standCode ? (
            <Stamp tone="info">{supplier.supplier.standCode}</Stamp>
          ) : undefined
        }
        facts={[
          ...(channel
            ? [
                {
                  label: t(`common:channel.${channel.kind}`),
                  value: (
                    <span className="row-baseline">
                      {(() => {
                        const ChannelIcon = CHANNEL_ICONS[channel.kind];
                        return <ChannelIcon />;
                      })()}
                      <span className="num">{channel.address}</span>
                    </span>
                  ),
                },
              ]
            : []),
          ...(supplier.supplier?.orderCutoff
            ? [{ label: t('cutoff'), value: <span className="num">{supplier.supplier.orderCutoff}</span> }]
            : []),
          ...(supplier.supplier?.runDays.length
            ? [
                {
                  label: t('contacts:runDays'),
                  value: (
                    <span className="num">
                      {supplier.supplier.runDays.map((d) => t(`weekday.${d}`)).join(' · ')}
                    </span>
                  ),
                },
              ]
            : []),
        ]}
        actions={
          <>
            <Button
              variant="quiet"
              size="sm"
              iconOnly
              aria-label={t('common:actions.print')}
              icon={<IconPrint />}
              onClick={() => window.print()}
            />
            <Button
              variant="quiet"
              size="sm"
              iconOnly
              aria-label={t('common:actions.close')}
              icon={<IconClose />}
              onClick={onClose}
            />
          </>
        }
      />

      <div className="pd-sup__manifest-body">
        <Section
          title={
            <>
              {t('bringFor')} <span className="num">{format.date(day, 'weekday')}</span>
            </>
          }
          actions={
            estimated > 0 ? (
              <span className="num faint">
                ≈ {format.money({ minor: estimated, currency: 'EUR' })}
              </span>
            ) : undefined
          }
        >
          {!group || group.lines.length === 0 ? (
            <EmptyState compact title={t('sheetEmpty')} body={t('sheetEmptyBody')} />
          ) : (
            <ul className="pd-sup__sheet">
              {group.lines.map((row) => {
                const product = productsById.get(row.productId);
                const open = openLine === row.productId;
                return (
                  <li
                    key={row.productId}
                    className="pd-sup__sheet-row"
                    data-hc={product?.handlingClass ?? 'neutral'}
                    data-open={open || undefined}
                  >
                    <span className="pd-band" aria-hidden="true" />
                    <button
                      type="button"
                      className="pd-sup__sheet-main"
                      aria-expanded={open}
                      onClick={() => setOpenLine(open ? null : row.productId)}
                    >
                      <span className="pd-sup__sheet-name">
                        <span className="truncate">
                          {product
                            ? productName(product, i18n.resolvedLanguage ?? 'en')
                            : row.productName}
                        </span>
                        <Code>{row.sku}</Code>
                      </span>
                      <span className="num faint">
                        {t('orderCount', { count: row.orders.length })}
                      </span>
                      <span className="num pd-sup__sheet-qty">{format.quantity(row.total)}</span>
                    </button>

                    {open ? (
                      <ul className="pd-sup__sheet-orders">
                        {row.orders.map((o) => (
                          <li key={`${o.orderId}-${o.quantity.value}`}>
                            <Code>{o.orderNumber}</Code>
                            <span className="truncate">{o.customerName}</span>
                            <Stamp tone={ORDER_TONE[o.status]} size="sm">
                              {t(`orders:status.${o.status}`)}
                            </Stamp>
                            <span className="num">{format.quantity(o.quantity)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {supplier.notes ? (
          <Section title={t('common:labels.notes')}>
            <FactList columns={1} items={[{ label: t('common:labels.notes'), value: supplier.notes }]} />
          </Section>
        ) : null}
      </div>
    </div>
  );
}
